// =============================================================================
// Issue #71: pin-movement debounce + cancellation helpers.
//
// The Delivery address checkout needs to debounce reverse-geocode requests
// on map pin movement so a customer dragging the pin does not flood the
// geocoder with one request per pixel. The previous implementation fired
// `reverseGeocode(lat, lng)` on every map click / pin drag end, which
// could produce several in-flight requests for the same moving pin.
//
// This helper exposes a tiny API:
//
//   const handle = createPinDebounce({
//     debounceMs: 450,
//     onSettle: (lat, lng, signal) => reverseGeocode(lat, lng, signal),
//   });
//   handle.move({ lat: -34.905, lng: -56.17 }); // first tap
//   handle.move({ lat: -34.906, lng: -56.17 }); // cancels previous, restarts
//   handle.cancel();                            // unmount / navigation away
//
// The helper returns an `AbortController.signal` so the onSettle callback
// can propagate cancellation to its underlying `fetch`. We deliberately
// expose this as a plain function (not a React hook) so it can be unit
// tested without a DOM and so the React component can hold the handle in
// a `useRef` for unmount-stable identity.
// =============================================================================

export type CreatePinDebounceArgs = {
  /** Milliseconds to wait after the last `move()` before invoking `onSettle`. */
  debounceMs: number;
  /**
   * Invoked once per debounce window. Receives the latest coordinates plus
   * an `AbortSignal` that fires if a subsequent `move()` arrives or if
   * `cancel()` is called before the request resolves.
   */
  onSettle: (input: { lat: number; lng: number; signal: AbortSignal }) => void | Promise<void>;
  /** Optional clock for tests. Defaults to `Date.now`. */
  now?: () => number;
};

export type PinDebounceHandle = {
  /**
   * Schedule a debounced call. The latest coordinates win: any pending
   * call is cancelled, its inflight signal aborted, and a fresh window
   * starts.
   */
  move: (next: { lat: number; lng: number }) => void;
  /** Cancel any pending call and abort the inflight signal. */
  cancel: () => void;
  /**
   * The signal of the currently pending or in-flight call, or `null` after
   * the callback settles. Exposed so the caller (and tests) can observe
   * the cancellation contract; the value is read-only.
   */
  currentSignal: () => AbortSignal | null;
};

export function createPinDebounce(args: CreatePinDebounceArgs): PinDebounceHandle {
  let pendingTimer: ReturnType<typeof setTimeout> | null = null;
  let controller: AbortController | null = null;
  let latest: { lat: number; lng: number } | null = null;

  function clear(): void {
    if (pendingTimer != null) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }
    if (controller != null) {
      controller.abort();
      controller = null;
    }
  }

  return {
    cancel() {
      clear();
      latest = null;
    },
    currentSignal() {
      return controller?.signal ?? null;
    },
    move(next) {
      latest = next;
      clear();
      const nextController = new AbortController();
      controller = nextController;
      pendingTimer = setTimeout(() => {
        pendingTimer = null;
        const coords = latest;
        const signal = nextController.signal;
        if (coords == null) {
          if (controller === nextController) {
            controller = null;
          }
          return;
        }
        const result = args.onSettle({ lat: coords.lat, lng: coords.lng, signal });
        if (result && "then" in result) {
          void Promise.resolve(result).finally(() => {
            if (controller === nextController) {
              controller = null;
            }
          });
        } else if (controller === nextController) {
          controller = null;
        }
      }, args.debounceMs);
    },
  };
}
